import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import logoSmall from "@/assets/images/logo-small.svg";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import permissionsService from "@/services/permissionsService";
import { getUnifiedNavigation } from "@/navigation/unifiedNavigation";
import { NavigationItem } from "@/navigation/types";
import {
  selectActiveApp,
  setActiveApp,
} from "@/app/globalSlices/appStripSlice";
import { useSidebar } from "@/components/ui/sidebar";
import { appStripItems } from "./appStripConfig";
import { AppStripItemConfig, AppStripId } from "./appStripTypes";
import AppStripItem from "./AppStripItem";

interface AppStripProps {
  onAppClick?: (appId: AppStripId) => void;
}

/**
 * Flatten navigation items recursively to get all items including nested children
 */
function flattenNavigationItems(items: NavigationItem[]): NavigationItem[] {
  const result: NavigationItem[] = [];
  items.forEach((item) => {
    result.push(item);
    if (item.children) {
      result.push(...flattenNavigationItems(item.children));
    }
  });
  return result;
}

/**
 * Check if a navigation item is visible based on permissions and CertainPath status
 */
function isNavItemVisible(
  item: NavigationItem,
  hasPermission: (p: string) => boolean,
  hasCertainPathCompany: boolean,
  hasCertainPathEmployee: boolean,
): boolean {
  // Check permissions
  let permissionsPass = true;
  if (item.permissions && item.permissions.length > 0) {
    permissionsPass = item.permissions.some((p) => hasPermission(p));
  }

  // Check CertainPath-only items (active company)
  let cpPass = true;
  if (item.isCertainPathOnly) {
    cpPass = hasCertainPathCompany;
  }

  // Check CertainPath-staff-only items (employee's own company)
  let cpEmployeePass = true;
  if (item.isCertainPathEmployeeOnly) {
    cpEmployeePass = hasCertainPathEmployee;
  }

  return permissionsPass && cpPass && cpEmployeePass;
}

/**
 * Check if an app strip item should be visible based on:
 * 1. Direct permissions (if specified)
 * 2. Whether any of its linked nav items are visible
 * 3. CertainPath company requirement
 */
function isAppStripItemVisible(
  item: AppStripItemConfig,
  allNavItems: NavigationItem[],
  hasPermission: (p: string) => boolean,
  hasCertainPathCompany: boolean,
  hasCertainPathEmployee: boolean,
): boolean {
  // Check CertainPath-only constraint first
  if (item.isCertainPathOnly && !hasCertainPathCompany) {
    return false;
  }

  // Check CertainPath-staff-only constraint (employee's own company)
  if (item.isCertainPathEmployeeOnly && !hasCertainPathEmployee) {
    return false;
  }

  // If item has direct permissions, check those
  if (item.permissions && item.permissions.length > 0) {
    const hasAnyPermission = item.permissions.some((p) => hasPermission(p));
    if (hasAnyPermission) {
      return true;
    }
    // If no direct permissions matched and no navItemInternalNames, hide the item
    if (!item.navItemInternalNames || item.navItemInternalNames.length === 0) {
      return false;
    }
  }

  // If item has navItemInternalNames, check if ANY of those nav items are visible
  if (item.navItemInternalNames && item.navItemInternalNames.length > 0) {
    const hasVisibleNavItem = item.navItemInternalNames.some((internalName) => {
      const navItem = allNavItems.find((n) => n.internalName === internalName);
      if (!navItem) return false;
      return isNavItemVisible(
        navItem,
        hasPermission,
        hasCertainPathCompany,
        hasCertainPathEmployee,
      );
    });
    return hasVisibleNavItem;
  }

  // If no permissions or navItemInternalNames, item is visible by default
  return true;
}

export default function AppStrip({ onAppClick }: AppStripProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeApp = useSelector(selectActiveApp);
  const { isMobile, setOpenMobile } = useSidebar();
  const { hasPermission, hasCertainPathCompany, hasCertainPathEmployee } =
    permissionsService();

  // Get all navigation items flattened
  const unifiedNav = getUnifiedNavigation();
  const allNavItems = flattenNavigationItems(unifiedNav);
  const isCertainPath = hasCertainPathCompany();
  const isCertainPathEmployee = hasCertainPathEmployee();

  // Filter items based on visibility rules
  const visibleItems = appStripItems.filter((item) =>
    isAppStripItemVisible(
      item,
      allNavItems,
      hasPermission,
      isCertainPath,
      isCertainPathEmployee,
    ),
  );

  const handleItemClick = (item: AppStripItemConfig) => {
    // Skip handling for external items (they handle their own clicks)
    if (item.isExternal) {
      return;
    }

    dispatch(setActiveApp(item.id as AppStripId));

    // Standalone apps navigate directly to their route
    if (item.standalone && item.defaultRoute) {
      navigate(item.defaultRoute);
    }

    if (isMobile) {
      // On mobile, open the sidebar sheet (unless standalone app)
      if (!item.standalone) {
        setOpenMobile(true);
      }
    } else {
      // On desktop, notify parent (so it can expand sidebar)
      onAppClick?.(item.id as AppStripId);
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn(
          "flex h-full w-14 flex-shrink-0 flex-col items-center overflow-hidden",
          "bg-white dark:bg-slate-900",
          "border-r border-gray-200 dark:border-slate-800/50",
          "py-3",
        )}
        data-appstrip
      >
        {/* Logo at top */}
        <div className="mb-4 flex h-10 w-10 items-center justify-center">
          <img alt="CertainPath" className="h-8 w-8" src={logoSmall} />
        </div>

        {/* Separator */}
        <div className="mb-3 h-px w-8 bg-slate-700/50" />

        {/* App icons */}
        <nav className="flex flex-1 flex-col gap-1">
          {visibleItems.map((item) => (
            <AppStripItem
              isActive={!item.isExternal && activeApp === item.id}
              item={item}
              key={item.id}
              onClick={() => handleItemClick(item)}
            />
          ))}
        </nav>
      </div>
    </TooltipProvider>
  );
}
